import { Routes } from '@angular/router';
import { aiModuleGuard } from './guards/ai-module.guard';
import { featureFlagGuard } from './guards/feature-flag.guard';
import { permissionGuard } from './guards/permission.guard';
import { systemGuard } from './guards/system.guard';
import { AuthGuard } from '@auth0/auth0-angular';
import { NotFoundComponent } from './components/errors/not-found/not-found.component';
import { AccessDeniedComponent } from './components/errors/access-denied/access-denied.component';
import { ErrorPageComponent } from './components/errors/error/error.component';
import { UnauthorizedComponent } from './components/errors/unauthorized/unauthorized.component';
import { PERMISSION } from 'utils/permissions/permission-catalog';
import { FEATURE_FLAGS } from './shared/utils/feature-flags';

export const routes: Routes = [
  {
    path: 'callback',
    loadComponent: () =>
      import('./features/callback/callback.component').then(
        (m) => m.CallbackComponent
      ),
  },
  {
    // Escape-hatch sign-out. Intentionally NO canActivate guard — AuthGuard
    // would bounce a broken/expired session into loginWithRedirect, defeating
    // the purpose of a route that must always be able to end the session.
    path: 'logout',
    loadComponent: () =>
      import('./features/logout/logout.component').then(
        (m) => m.LogoutComponent
      ),
  },
  {
    // "Your account is ready": where a password-change ticket returns the
    // user (reached through /open/welcome, see open-link.ts). PUBLIC on
    // purpose — the user has no session yet; the screen sends a phone to the
    // mobile app and a desktop to sign-in.
    path: 'welcome',
    loadComponent: () =>
      import('./features/account-ready/account-ready.component').then(
        (m) => m.AccountReadyComponent
      ),
  },
  {
    // PUBLIC, login-free issue form. Intentionally NO canActivate guard —
    // access is gated by the share-link token, validated server-side.
    path: 'public/issue-form/:token',
    loadComponent: () =>
      import('./features/public/public-issue-form.component').then(
        (m) => m.PublicIssueFormComponent
      ),
  },
  {
    path: 'home',
    redirectTo: 'homepage',
    pathMatch: 'full',
  },
  {
    path: '',
    redirectTo: 'homepage',
    pathMatch: 'full',
  },
  {
    path: 'homepage',
    loadComponent: () =>
      import('./components/homepage/homepage.component').then(
        (m) => m.HomepageComponent
      ),
    canActivate: [AuthGuard],
  },
  {
    // Dashboards are seat-gated (roles overhaul §8): dashboards.view only
    // ever comes from a per-member grant, seeded to org/tenant admins.
    path: 'dashboard',
    loadComponent: () =>
      import('./components/looker-dashboard/looker-dashboard.component').then(
        (m) => m.LookerDashboardComponent
      ),
    canActivate: [AuthGuard, permissionGuard(PERMISSION.dashboards.view)],
  },
  {
    // The /system area: operations on GLOBAL entities (users, organizations,
    // tenants, cross-org tooling, platform audit) — uber-only, guarded at the
    // PARENT so children inherit. Org-context configuration stays org-side.
    path: 'system',
    canActivate: [AuthGuard, systemGuard],
    children: [
      {
        path: 'users',
        loadComponent: () =>
          import(
            './components/system/system-users/system-users.component'
          ).then((m) => m.SystemUsersComponent),
      },
      {
        path: 'organizations',
        loadComponent: () =>
          import('./components/organization/organization.component').then(
            (m) => m.OrganizationComponent
          ),
      },
      {
        path: 'tenants',
        loadComponent: () =>
          import('./components/tenant/tenant.component').then(
            (m) => m.TenantComponent
          ),
      },
      {
        path: 'clone-organization',
        loadComponent: () =>
          import('./components/org-clone/org-clone.component').then(
            (m) => m.OrgCloneComponent
          ),
      },
      {
        path: 'share-links',
        loadComponent: () =>
          import(
            './components/issue-share-link/share-link-list.component'
          ).then((m) => m.ShareLinkListComponent),
      },
      {
        path: 'audit',
        loadComponent: () =>
          import('./components/console/console.component').then(
            (m) => m.ConsoleComponent
          ),
      },
      {
        path: 'roles',
        loadComponent: () =>
          import(
            './components/system/system-roles/system-roles.component'
          ).then((m) => m.SystemRolesComponent),
      },
      {
        path: 'licenses',
        loadComponent: () =>
          import(
            './components/system/system-licenses/system-licenses.component'
          ).then((m) => m.SystemLicensesComponent),
      },
    ],
  },
  // Courtesy redirects for the pre-/system URLs (bookmarks, older deep links).
  { path: 'console', redirectTo: 'system/audit', pathMatch: 'full' },
  {
    path: 'organizations',
    redirectTo: 'system/organizations',
    pathMatch: 'full',
  },
  {
    path: 'organization',
    loadComponent: () =>
      import(
        './components/organization-details/organization-details.component'
      ).then((m) => m.OrganizationDetailsComponent),
    canActivate: [AuthGuard, permissionGuard(PERMISSION.org.settings.manage)],
  },
  {
    // Module provisioning is a platform operation, uber-enforced server-side.
    path: 'modules',
    loadComponent: () =>
      import('./components/module/module.component').then(
        (m) => m.ModuleComponent
      ),
    canActivate: [AuthGuard, systemGuard],
  },
  {
    path: 'menu-config',
    loadComponent: () =>
      import('./components/menu-config/menu-config.component').then(
        (m) => m.MenuConfigComponent
      ),
    canActivate: [AuthGuard, permissionGuard(PERMISSION.config.menu.manage)],
  },
  {
    // The /ai area: feature-flagged AND DomainGRC-admin-only, guarded at the
    // PARENT so children inherit — including the '' redirect, which carried no
    // guard of its own. `canActivateChild` re-checks on navigation *within* the
    // section, so switching to an org where the user is not an admin ejects
    // them instead of leaving the already-activated parent standing.
    path: 'ai',
    loadComponent: () =>
      import('./components/ai/ai-layout/ai-layout.component').then(
        (m) => m.AiLayoutComponent
      ),
    canActivate: [AuthGuard, aiModuleGuard],
    canActivateChild: [aiModuleGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'chat',
      },
      {
        path: 'chat',
        loadComponent: () =>
          import('./components/ai/ai.component').then((m) => m.AiComponent),
      },
      {
        path: 'history',
        loadComponent: () =>
          import('./components/ai/ai-history/ai-history.component').then(
            (m) => m.AiHistoryComponent
          ),
      },
      {
        path: 'knowledge',
        loadComponent: () =>
          import(
            './features/knowledge/knowledge-manager/knowledge-manager.component'
          ).then((m) => m.KnowledgeManagerComponent),
      },
    ],
  },
  { path: 'tenants', redirectTo: 'system/tenants', pathMatch: 'full' },
  {
    path: 'users',
    loadComponent: () =>
      import('./components/user/user.component').then((m) => m.UserComponent),
    canActivate: [AuthGuard, permissionGuard(PERMISSION.members.view)],
  },
  {
    path: 'issue-type-manager',
    loadComponent: () =>
      import('./components/issue-manager/issue-type-manager.component').then(
        (m) => m.IssueTypeManagerComponent
      ),
    canActivate: [
      AuthGuard,
      permissionGuard(PERMISSION.config.issueTypes.manage),
    ],
  },
  {
    // Org-wide field definitions placed on issue types from the builder;
    // same key as the issue-type manager, since that is what they configure.
    path: 'shared-fields',
    loadComponent: () =>
      import(
        './components/shared-fields-admin/shared-fields-admin.component'
      ).then((m) => m.SharedFieldsAdminComponent),
    canActivate: [
      AuthGuard,
      permissionGuard(PERMISSION.config.issueTypes.manage),
    ],
  },
  {
    path: 'issue-type-categories',
    loadComponent: () =>
      import(
        './components/issue-type-category/issue-type-category.component'
      ).then((m) => m.IssueTypeCategoryComponent),
    canActivate: [
      AuthGuard,
      permissionGuard(PERMISSION.config.issueTypes.manage),
    ],
  },
  {
    path: 'admin/clone-organization',
    redirectTo: 'system/clone-organization',
    pathMatch: 'full',
  },
  {
    path: 'admin/share-links',
    redirectTo: 'system/share-links',
    pathMatch: 'full',
  },
  {
    path: 'admin/automations',
    loadComponent: () =>
      import(
        './components/automations/automation-list/automation-list.component'
      ).then((m) => m.AutomationListComponent),
    canActivate: [
      AuthGuard,
      permissionGuard(PERMISSION.config.automations.manage),
    ],
  },
  {
    path: 'admin/contacts',
    loadComponent: () =>
      import('./components/contacts/contacts.component').then(
        (m) => m.ContactsComponent
      ),
    canActivate: [AuthGuard, permissionGuard(PERMISSION.contacts.manage)],
  },
  {
    path: 'admin/automations/:id',
    loadComponent: () =>
      import(
        './components/automations/automation-builder/automation-builder.component'
      ).then((m) => m.AutomationBuilderComponent),
    canActivate: [
      AuthGuard,
      permissionGuard(PERMISSION.config.automations.manage),
    ],
  },
  {
    path: 'issue-type/create',
    loadComponent: () =>
      import(
        './components/issue-manager/create-issue-type/create-issue-type.component'
      ).then((m) => m.CreateIssueTypeComponent),
    canActivate: [
      AuthGuard,
      permissionGuard(PERMISSION.config.issueTypes.manage),
    ],
  },
  {
    path: 'issue-type/edit/:id',
    loadComponent: () =>
      import(
        './components/issue-manager/edit-issue-type/edit-issue-type.component'
      ).then((m) => m.EditIssueTypeComponent),
    canActivate: [
      AuthGuard,
      permissionGuard(PERMISSION.config.issueTypes.manage),
    ],
  },
  {
    path: 'boards/:prefix',
    loadComponent: () =>
      import(
        './components/board/board-by-prefix/board-by-prefix.component'
      ).then((m) => m.BoardByPrefixComponent),
    canActivate: [AuthGuard],
  },
  {
    path: 'issues/:prefix',
    loadComponent: () =>
      import(
        './components/issue/issue-by-prefix/issue-by-prefix.component'
      ).then((m) => m.IssueByPrefixComponent),
    canActivate: [AuthGuard],
  },
  {
    path: 'all-issues',
    loadComponent: () =>
      import('./components/issue/issues-grid/issues-grid.component').then(
        (m) => m.IssuesGridComponent
      ),
    canActivate: [
      AuthGuard,
      // Report-building surface, matching the nav item: reports.create
      // org-wide (a binding must not grant org-spanning report building),
      // issues.view.all held anywhere (a module-bound member has issues to
      // report on). Server-side visibility decides the rows either way.
      permissionGuard([
        { key: PERMISSION.reports.create, orgWide: true },
        { key: PERMISSION.issues.view.all, orgWide: false },
      ]),
    ],
  },
  {
    path: 'reports/saved/:id',
    loadComponent: () =>
      import('./components/issue/issues-grid/issues-grid.component').then(
        (m) => m.IssuesGridComponent
      ),
    canActivate: [AuthGuard, permissionGuard(PERMISSION.reports.view)],
  },
  {
    path: 'reports',
    loadComponent: () =>
      import('./components/reports/reports.component').then(
        (m) => m.ReportsComponent
      ),
    canActivate: [AuthGuard, permissionGuard(PERMISSION.reports.view)],
  },
  {
    path: 'issue/:key',
    loadComponent: () =>
      import('./components/issue/issue.component').then(
        (m) => m.IssueComponent
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'audit/:id',
    loadComponent: () =>
      import('./components/audit/audit.component').then(
        (m) => m.AuditComponent
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'search',
    loadComponent: () =>
      import(
        './components/search-results-page/search-results-page.component'
      ).then((m) => m.SearchResultsPageComponent),
    canActivate: [AuthGuard],
  },
  {
    // Org-context configuration. Per-child guards (roles overhaul §11.1):
    // permission-gated pages use the catalog key their backend enforces;
    // pages whose backend is uber-enforced keep systemGuard so a page can
    // never render for someone whose every API call would 403.
    path: 'admin',
    children: [
      {
        path: 'roles',
        loadComponent: () =>
          import('./components/roles/roles.component').then(
            (m) => m.RolesComponent
          ),
        canActivate: [permissionGuard(PERMISSION.roles.manage)],
      },
      {
        // Custom calendars (docs/calendar-events-design.md §13). Org-wide like
        // the Calendar itself: a calendar spans modules.
        path: 'calendars',
        loadComponent: () =>
          import('./components/calendar-admin/calendars-admin.component').then(
            (m) => m.CalendarsAdminComponent
          ),
        canActivate: [
          featureFlagGuard(FEATURE_FLAGS.CALENDAR_ENABLED),
          permissionGuard(PERMISSION.calendar.manage, { orgWide: true }),
        ],
      },
      {
        path: 'module-type-configuration',
        loadComponent: () =>
          import(
            './components/link-configuration/link-configuration.component'
          ).then((m) => m.LinkConfigurationComponent),
        canActivate: [systemGuard],
      },
      {
        path: 'tab-labels',
        loadComponent: () =>
          import(
            './components/tab-label-alias-graph/tab-label-alias-graph.component'
          ).then((m) => m.TabLabelAliasGraphComponent),
        canActivate: [permissionGuard(PERMISSION.config.menu.manage)],
      },
      // Courtesy redirect: the previous URL surfaced under `tab-label-aliases`
      // when the admin page was a table. Anyone still navigating there gets
      // routed to the new visual graph at `/admin/tab-labels`.
      {
        path: 'tab-label-aliases',
        redirectTo: 'tab-labels',
        pathMatch: 'full',
      },
      {
        path: 'search-config',
        loadComponent: () =>
          import('./components/search-config/search-config.component').then(
            (m) => m.SearchConfigComponent
          ),
        canActivate: [systemGuard],
      },
      {
        path: 'dashboard-config',
        loadComponent: () =>
          import(
            './components/dashboard-config/dashboard-config.component'
          ).then((m) => m.DashboardConfigComponent),
        canActivate: [systemGuard],
      },
      {
        path: 'integrations',
        loadComponent: () =>
          import('./components/integrations/integrations.component').then(
            (m) => m.IntegrationsComponent
          ),
        canActivate: [permissionGuard(PERMISSION.config.integrations.manage)],
      },
    ],
    canActivate: [AuthGuard],
  },
  {
    // No dedicated import permission exists in the catalog yet; org-admin
    // (the only seed role holding org.settings.manage) matches today's gate.
    path: 'history/import',
    loadComponent: () =>
      import(
        './components/importExportHistory/importExportHistory.component'
      ).then((m) => m.ImportExportHistoryComponent),
    canActivate: [AuthGuard, permissionGuard(PERMISSION.org.settings.manage)],
  },
  {
    path: 'calendar',
    loadComponent: () =>
      import(
        './components/calendar/calendar-page/calendar-page.component'
      ).then((m) => m.CalendarPageComponent),
    // Org-wide, matching the sidenav item: the calendar spans every module.
    // Feature-flagged like the sidenav item (off in production).
    canActivate: [
      AuthGuard,
      featureFlagGuard(FEATURE_FLAGS.CALENDAR_ENABLED),
      permissionGuard(PERMISSION.calendar.view, { orgWide: true }),
    ],
  },
  {
    path: 'attachments',
    loadComponent: () =>
      import(
        './components/attachment-manager/attachment-manager.component'
      ).then((m) => m.AttachmentManagerComponent),
    // Org-wide: the library spans every module, so a module binding alone
    // does not open it (§7.6). Matches the sidenav item's `orgWide`.
    canActivate: [
      AuthGuard,
      permissionGuard(PERMISSION.issues.view.own, { orgWide: true }),
    ],
  },
  {
    path: 'user-groups',
    loadComponent: () =>
      import('./components/user-groups/user-groups.component').then(
        (m) => m.UserGroupsComponent
      ),
    canActivate: [AuthGuard, permissionGuard(PERMISSION.userGroups.manage)],
  },
  {
    path: 'user-groups/:id',
    loadComponent: () =>
      import(
        './components/user-groups/user-group-member/user-group-members.component'
      ).then((m) => m.UserGroupMembersComponent),
    canActivate: [AuthGuard, permissionGuard(PERMISSION.userGroups.manage)],
  },
  {
    path: 'account-settings',
    loadComponent: () =>
      import('./components/account-settings/account-settings.component').then(
        (m) => m.AccountSettingsComponent
      ),
    canActivate: [AuthGuard],
  },
  { path: 'access-denied', component: AccessDeniedComponent },
  { path: 'unauthorized', component: UnauthorizedComponent },
  { path: 'error', component: ErrorPageComponent },
  { path: '**', component: NotFoundComponent },
];
